/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';

import { environment } from '@env/environment';

import { FormGroup } from '@angular/forms';
import {
  Agency,
  AppComment,
  AppInformation,
  ApplicationActionResponse,
  CommentValue,
  CreateQbAppResponse,
  DynamicControlType,
  DynamicForm,
  DynamicFormData,
  DynamicFormValues,
  DynamicFormValuesSections,
  EventListItem,
  GenericResponse,
  GetActiveAgencyApps,
  GetQbAppListModel,
  GetQbAppResponse,
  PrequalAppScoring,
  SaveApplicationRequest,
  StepVerified,
} from '@app/models';
import { delay, map, tap } from 'rxjs/operators';
import { AppCommentsService } from './app-comments.service';

import { CopyApp } from '@app/models/copyApp.model';
import { ApplicationSummary, FieldCompliance } from '@src/app/LAUSD/models/lausd-agency.model';
import { AppInfoService } from './app-info.service';
import { LoginService } from './login.service';

// ON DEVELOPMENT, WE CAN REPLACE THE QUESTIONNAIRE LOADED BY COMMENTING OUT/UNCOMMENTING THIS CODE.
// Don't commit this uncommented. It would be dumb.
// import * as CupccaaQuestionnaire from '@app/shared/current-cupccaa-questionnaire.json';
// import * as PrequalQuestionnaire from '@app/shared/current-prequal-questionnaire.json';
// import * as LausdQuestionnaire from '@app/shared/lausd-prime-questionnaire.json';

@Injectable({
  providedIn: 'root',
})
export class AppService {
  private _$appAgencies: BehaviorSubject<number[]> = new BehaviorSubject<number[]>([]);
  get $appAgencies() {
    return this._$appAgencies.asObservable().pipe(delay(0));
  }
  setAgencies(agencyIds: number[]) {
    this._$appAgencies.next(agencyIds);
  }

  private _$selectedAppAgencies: ReplaySubject<Agency[]>;

  constructor(
    private http: HttpClient,
    private appComments: AppCommentsService,
    private login: LoginService,
    private appInfo: AppInfoService
  ) {}

  canCreateApplication(questionnaireTypeId: number): Observable<number> {
    return this.http.get<number>(
      `${environment.apiUrl}/api/general/Application/CanCreateApplication?questionnaireTypeId=${questionnaireTypeId}`
    );
  }

  getAvailableAppsToBeCopied(questionnaireTypeId: number): Observable<CopyApp[]> {
    return this.http.get<CopyApp[]>(`${environment.apiUrl}/api/vendor/Application/GetAvailableAppsToBeCopied`, {
      params: {
        typeId: questionnaireTypeId,
      },
    });
  }

  createApp(questionnaireTypeId: number, duplicateAnswersForNewApp = false): Observable<CreateQbAppResponse> {
    const payload = { questionnaireTypeId, duplicateAnswersForNewApp };
    return this.http.post<CreateQbAppResponse>(
      `${environment.apiUrl}/api/vendor/Application/CreateNewApplication`,
      payload
    );
  }

  copyExistingApp(appId: number): Observable<number> {
    return this.http.post<number>(`${environment.apiUrl}/api/vendor/Application/CopyExistingApplication`, { appId });
  }

  saveApp(
    appId: number,
    answers: string,
    submit: boolean = false,
    hasChanges: boolean = true
  ): Observable<PrequalAppScoring[]> {
    const payload: SaveApplicationRequest = {
      id: appId,
      answerBody: answers,
      saveAndSubmit: submit,
      hasChanges,
    };

    return this.http.post<PrequalAppScoring[]>(
      `${environment.apiUrl}/api/vendor/SaveApplication/SaveApplication`,
      payload
    );
  }

  returnApp(appId: number, reason: string = ''): Observable<ApplicationActionResponse> {
    const payload = {
      applicationId: appId,
      reasonForAction: reason,
    };

    return this.http.post<ApplicationActionResponse>(
      `${environment.apiUrl}/api/agency/Application/ReturnApplication`,
      payload
    );
  }

  rejectApp(appId: number, reason: string = ''): Observable<ApplicationActionResponse> {
    const payload = {
      applicationId: appId,
      reasonForAction: reason,
    };

    return this.http.post<ApplicationActionResponse>(
      `${environment.apiUrl}/api/agency/Application/RejectApplication`,
      payload
    );
  }

  reactivateApp(appId: number, reason: string = ''): Observable<ApplicationActionResponse> {
    const payload = {
      applicationId: appId,
      reasonForAction: reason,
    };

    return this.http.post<ApplicationActionResponse>(
      `${environment.apiUrl}/api/agency/Application/ReactivateApplication`,
      payload
    );
  }

  closeApp(appId: number, reason: string): Observable<ApplicationActionResponse> {
    const payload = {
      applicationId: appId,
      reasonForAction: reason,
    };

    return this.http.post<ApplicationActionResponse>(
      `${environment.apiUrl}/api/agency/Application/CloseApplication`,
      payload
    );
  }

  closeBatchApp(
    appId: number,
    agencyIds: number[],
    reason: string,
    contractorComments: string,
    contractorCommentsForAgencies: { [key: string]: CommentValue }
  ): Observable<ApplicationActionResponse> {
    const payload = {
      applicationId: appId,
      agencyIdList: agencyIds,
      reasonForAction: reason,
      contractorComments,
      contractorCommentsForAgencies,
    };

    return this.http.post<ApplicationActionResponse>(
      `${environment.apiUrl}/api/agency/Application/SetBatchApplicationClosed`,
      payload
    );
  }

  setBatchApplicationReadyForInternalReview(
    appId: number,
    agencyIds: number[],
    toggleOff = false
  ): Observable<boolean> {
    return this.http.post<any>(
      `${environment.apiUrl}/api/agency/Application/SetBatchApplicationReadyForInternalReview`,
      {
        applicationId: appId,
        agencyIds: agencyIds,
        toggleOff,
      }
    );
  }

  setBatchApplicationReadyForFinalReview(appId: number, agencyIdList: number[]): Observable<boolean> {
    return this.http.post<any>(`${environment.apiUrl}/api/agency/Application/SetBatchApplicationReadyForFinalReview`, {
      applicationId: appId,
      agencyIdList,
    });
  }

  sendFinalApprovalAppBackToQBAT(appId: number, reason: string): Observable<boolean> {
    return this.http.post<any>(`${environment.apiUrl}/api/agency/Application/SendFinalApprovalAppBackToQBAT`, {
      applicationId: appId,
      reasonForAction: reason,
    });
  }

  approveApp(
    appId: number,
    reason: string,
    rate?: number,
    rateNotApplicable?: boolean
  ): Observable<ApplicationActionResponse> {
    const payload = {
      applicationId: appId,
      reasonForAction: reason,
      approvedRate: rate ?? 0,
      rateNotApplicable: rateNotApplicable ?? false,
    };

    return this.http.post<ApplicationActionResponse>(
      `${environment.apiUrl}/api/agency/Application/ApproveApplication`,
      payload
    );
  }

  approveBatchApp(appId: number, agencyIds: number[], reason: string): Observable<ApplicationActionResponse> {
    const payload = {
      applicationId: appId,
      agencyIdList: agencyIds,
      reasonForAction: reason,
    };

    return this.http.post<ApplicationActionResponse>(
      `${environment.apiUrl}/api/agency/Application/SetBatchCupccaaApplicationApproved`,
      payload
    );
  }

  removeAgencyFromApp(appId: number, agencyId: number, closeApp: boolean) {
    const payload = {
      applicationId: appId,
      agencyId: agencyId,
      closeApplication: closeApp,
    };

    return this.http.post<ApplicationActionResponse>(
      `${environment.apiUrl}/api/vendor/Application/RemoveAgencyFromApplication`,
      payload
    );
  }

  deleteApp(appId: number) {
    return this.http.post<ApplicationActionResponse>(
      `${environment.apiUrl}/api/vendor/Application/DeleteUnsentApplication`,
      appId
    );
  }

  /** Get Application as a Vendor */
  getApp(appId: number): Observable<GetQbAppResponse> {
    return this.http.post<GetQbAppResponse>(`${environment.apiUrl}/api/vendor/Application/GetApplication`, appId).pipe(
      tap(val => {
        // Set app info to service.
        this.appInfo.setAppInfo(val);

        // Whenever we get an App, create a new FormGroup with each Section Comment Group.
        // Then stream the comments form group to it's own subject in the AppService
        const fg = new FormGroup({});

        if (val.comments) {
          this.addLoadedCommentControls(val.comments, fg);
        }
        this.appComments.comments = fg;
        this.appInfo.setAppInfo(val);
      })
      // ON DEVELOPMENT, WE CAN REPLACE THE QUESTIONNAIRE LOADED BY COMMENTING OUT/UNCOMMENTING THIS CODE.
      // Don't commit this uncommented. It would be dumb.

      // , map(resp => {
      //   if (!environment.production) {
      //     let app;
      //     if (environment.site == 'lausd') {
      //       app = LausdQuestionnaire;
      //     } else {
      //       app = resp.questionnaireTypeId == 1 ? PrequalQuestionnaire : CupccaaQuestionnaire;
      //     }
      //     resp.applicationBody = JSON.stringify(app);
      //     this.appInfo.setAppInfo(resp);
      //   }
      //   return resp;
      // })
    );
  }

  getAppInformation(appId: number): Observable<AppInformation> {
    return this.http.get<AppInformation>(
      `${environment.apiUrl}/api/general/Application/GetApplicationInformation?appId=${appId}`
    );
  }

  getAppEvents(appId: number): Observable<{ eventList: EventListItem[] }> {
    return this.http.get<{ eventList: EventListItem[] }>(
      `${environment.apiUrl}/api/general/Base/GetApplicationEvents?appId=${appId}`
    );
  }

  /** @Deprecated This technically doesn't work any more */
  getVendorApps(questionnaireTypeId: number): Observable<GetQbAppListModel> {
    return this.http.get<GetQbAppListModel>(
      `${environment.apiUrl}/api/vendor/Application/GetActiveApplications?questionnaireTypeId=${questionnaireTypeId}`
    );
  }

  getAllVendorApps(): Observable<{
    primeApps: GetQbAppListModel;
    subApps: GetQbAppListModel;
    safetyApps: GetQbAppListModel;
  }> {
    return this.http.get<{
      primeApps: GetQbAppListModel;
      subApps: GetQbAppListModel;
      safetyApps: GetQbAppListModel;
    }>(`${environment.apiUrl}/api/vendor/Application/GetActiveApplications`);
  }

  getVendorAgencies(): Observable<Agency[]> {
    return this.http
      .post<any>(`${environment.apiUrl}/api/general/Application/GetAgencies`, { questionnaireTypeId: 2 })
      .pipe(
        map(res => {
          const retValue = res.agencyList.map(agency => {
            if (agency.formName) {
              agency.formData = JSON.parse(agency.formData);
              agency.hasAddReqs = true;
            } else {
              if (!agency.formName) agency.formName = agency.agencyToken;

              if (!agency.formData?.QuestionList) {
                agency.formData = {};
                agency.formData.QuestionList = new Array<DynamicFormData>();
              }

              agency.hasAddReqs = false;
            }

            if (!this.hasCommentsForm(agency.formData.QuestionList)) {
              const commentFormData: DynamicFormData = {
                controlName: agency.formName + 'Comments',
                controlType: DynamicControlType.Comments,
                controlLabel: 'Comments',
                hidden: false,
                validators: { required: false },
              };

              agency.formData.QuestionList.push(commentFormData);
            }

            return agency;
          });
          return retValue;
        })
      );
  }

  getAgencies(
    options: {
      questionnaireTypeId?: number;
      appId?: number;
    } = {
      // Defaults to retrieving CUPCCAA applications
      questionnaireTypeId: 2,
    }
  ): Observable<Agency[]> {
    const endPoint: string = `${environment.apiUrl}/api/general/Application/GetAgencies`;

    return this.http.post<any>(endPoint, options).pipe(
      map(res => {
        const retValue = res.agencyList.map(agency => {
          if (agency.formName) {
            agency.formData = JSON.parse(agency.formData);
            agency.hasAddReqs = true;
          } else {
            if (!agency.formName) agency.formName = agency.agencyToken;

            if (!agency.formData?.QuestionList) {
              agency.formData = {};
              agency.formData.QuestionList = new Array<DynamicFormData>();
            }

            agency.hasAddReqs = false;
          }

          // console.log('before form data:', agency.formData);

          if (!this.hasCommentsForm(agency.formData.QuestionList)) {
            const commentFormData: DynamicFormData = {
              controlName: agency.formName + 'Comments',
              controlType: DynamicControlType.Comments,
              controlLabel: 'Comments',
              hidden: false,
              validators: { required: false },
            };

            agency.formData.QuestionList.push(commentFormData);
          }

          // console.log('form data:', agency.formData);
          return agency;
        });
        return retValue;
      })
    );
  }

  getSelectedAppAgencies(appId: number, force?: boolean): Observable<Agency[]> {
    if (!this._$selectedAppAgencies || force) {
      this._$selectedAppAgencies = new ReplaySubject(1);
      this.http
        .get<any>(`${environment.apiUrl}/api/general/Base/GetSelectedAppAgencies?appId=${appId}`)
        .pipe(
          map(res => {
            const retValue = res.agencyList.map(agency => {
              if (agency.formName) {
                agency.formData = JSON.parse(agency.formData);
                agency.hasAddReqs = true;
              } else {
                if (!agency.formName) agency.formName = agency.agencyToken;

                if (!agency.formData?.QuestionList) {
                  agency.formData = {};
                  agency.formData.QuestionList = new Array<DynamicFormData>();
                }

                agency.hasAddReqs = false;
              }

              // console.log('before form data:', agency.formData);
              agency.agencyListComment = agency.agencyListComment ?? null;

              if (!this.hasCommentsForm(agency.formData.QuestionList)) {
                const commentFormData: DynamicFormData = {
                  controlName: agency.formName + 'Comments',
                  controlType: DynamicControlType.Comments,
                  controlLabel: 'Comments',
                  hidden: false,
                  validators: { required: false },
                };

                agency.formData.QuestionList.push(commentFormData);
              }

              // console.log('form data:', agency.formData);
              return agency;
            });
            return retValue;
          })
        )
        .subscribe(res => {
          this._$selectedAppAgencies.next(res);
        });
    }

    return this._$selectedAppAgencies.asObservable();
  }

  getAgencyApp(appId: number): Observable<GetQbAppResponse> {
    return this.http.post<GetQbAppResponse>(`${environment.apiUrl}/api/agency/Application/GetApplication`, appId).pipe(
      map(val => {
        val.parsedApplicationBody = <DynamicForm>JSON.parse(val.applicationBody);
        return val;
      }),
      tap(val => {
        // Set app info to service.
        this.appInfo.setAppInfo(val);

        // Whenever we get an App, create a new FormGroup with each Section Comment Group.
        // Then stream the comments form group to it's own subject in the AppService
        const fg = new FormGroup({});
        const c = val.comments;

        // create a representation `StepVerified` of each step->section and whether that section is verified or not.
        // Skip any steps where there are no sections with comments enabled.
        // Note: This doesn't get run in LAUSD apps and therefore there is no StepsVerified
        // We could change this. Not sure how much work it would be though, for how much benefit.
        this._$selectedAppAgencies.subscribe((selectedAgencies: Agency[] = []) => {
          const stepsVerified: StepVerified[] = val.parsedApplicationBody.steps.map(step => {
            const hasCommentsEnabled = step.sections.some(section => section.commentsEnabled === true);
            const sections = {};

            // QUA-564: Add Stepper Checkmarks for Qbat as Sections are Verified
            // Additional Check for the First Step "Agency Selections"
            if (step.sections[0].formName === 'publicAgencySelections') {
              selectedAgencies.forEach(agency => {
                if (agency && agency.agencyToken) {
                  sections[agency.agencyToken] = {
                    verified: false,
                    areaOfConcern: false,
                    hasNote: false,
                  };
                }
              });
            }

            if (hasCommentsEnabled) {
              step.sections.forEach(section => {
                sections[section.formName] = {
                  verified: false,
                  areaOfConcern: false,
                  hasNote: false,
                };
              });
            }

            return {
              name: step.name,
              verified: !hasCommentsEnabled,
              areaOfConcern: false,
              hasNote: false,
              sections: sections,
            };
          });
          this.appComments.stepsVerified = stepsVerified;
        });

        // Take all the comments and create a new comment control for each one.
        // Any bugs encountered with this will give us a clearer idea of how we need to address this on the back end.
        this.addLoadedCommentControls(c, fg);
        this.appComments.comments = fg;
      })
    );
  }

  getAgencyApps(questionnaireTypeId: number = 0): Observable<GetActiveAgencyApps> {
    return this.http.get<GetActiveAgencyApps>(
      `${environment.apiUrl}/api/Agency/Application/GetActiveApplications?questionnaireTypeId=${questionnaireTypeId}`
    );
  }

  addQbatLogNote(appId: number, note: string): Observable<GenericResponse> {
    return this.http.post<GenericResponse>(`${environment.apiUrl}/api/agency/Application/AddQbatLogNote`, {
      appId,
      note,
    });
  }

  /**
   * For each application that is loaded (regardless of user type), we need to parse the answers into a DynamicFormValuesSection object.
   * If it's an older version, the parsed JSON obj will have a "Forms" property. Get rid of this if so.
   * Run this in each app-view component.
   * @param answerBody
   * @returns
   */
  parseLoadedFormAnswers(answerBody: string): DynamicFormValuesSections {
    const parsedAnswerBody = answerBody ? JSON.parse(answerBody) : [];

    // If the parsed `loadedFormAnswers` has a `forms` property, then we have saved this app in the older format (As in the type DynamicFormValues)
    let formSections;
    if (Object.prototype.hasOwnProperty.call(parsedAnswerBody, 'forms')) {
      formSections = (parsedAnswerBody as DynamicFormValues).forms;
    } else {
      formSections = parsedAnswerBody;
    }
    let sections: DynamicFormValuesSections = {};
    if (Array.isArray(formSections)) {
      // We used to store the answers straight as a FormGroup.GetValue() string, but now on the back-end we transform it into an key/value object of sectionName/sectionAnswers,
      // Which is what we did on the front-end anyway when loading the values, so we are now just eliminating the middleman.
      formSections.forEach(val => {
        sections = { ...sections, ...val };
      });
    } else {
      sections = formSections;
    }
    return sections;
  }

  public getFieldCompliace(appId: number): Observable<FieldCompliance> {
    return this.http.get<FieldCompliance>(`${environment.apiUrl}/api/agency/Application/GetFieldComplianceSection`, {
      params: { appId: appId },
    });
  }

  public saveFieldCompliance(fieldCompliance: FieldCompliance): any {
    return this.http.post<any>(
      `${environment.apiUrl}/api/agency/Application/SaveFieldComplianceSection`,
      fieldCompliance
    );
  }

  public getApplicationSummary(appId: number): Observable<ApplicationSummary> {
    return this.http.get<ApplicationSummary>(`${environment.apiUrl}/api/agency/Application/GetApplicationSummary`, {
      params: { appId: appId },
    });
  }

  public saveSingleApplicationAnswer(appId: number, controlName: string, answer: any): Observable<any> {
    return this.http.post<any>(`${environment.apiUrl}/api/agency/Application/SaveSingleApplicationAnswer`, {
      appId,
      controlName,
      answer,
    });
  }

  private hasCommentsForm(questionList: DynamicFormData[]) {
    let hasComments = false;

    questionList.forEach(question => {
      if (question.controlType == DynamicControlType.Comments) hasComments = true;
    });

    return hasComments;
  }

  /** Normalize API comment payloads into form control values (handles camelCase and PascalCase). */
  private addLoadedCommentControls(comments: { [x: string]: AppComment } | undefined, fg: FormGroup): void {
    if (!comments) {
      return;
    }

    Object.keys(comments).forEach(key => {
      const comment = comments[key];
      const agencyNotes = comment.agencyNotes ?? (comment as { AgencyNotes?: string }).AgencyNotes ?? '';
      const contractorComments =
        comment.contractorComments ?? (comment as { ContractorComments?: string }).ContractorComments ?? '';
      const isAgency =
        comment.isAgency ??
        comment.isAgencySelectionComment ??
        (comment as { IsAgencySelectionComment?: boolean }).IsAgencySelectionComment ??
        false;

      fg.addControl(
        key,
        this.appComments.createCommentControl(
          comment.verified ?? false,
          contractorComments,
          comment.areaOfConcern ?? false,
          agencyNotes,
          isAgency,
          comment.label
        )
      );
      this.appComments.updateStepsVerified(key, comment.verified, 'verified');
      this.appComments.updateStepsVerified(key, comment.areaOfConcern, 'areaOfConcern');
      this.appComments.updateStepsVerified(key, !!agencyNotes, 'hasNote');
    });
  }
}
